import { Component, OnInit, OnChanges, SimpleChanges, OnDestroy } from "@angular/core";
import { Location } from '@angular/common';
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
import { Router, ActivatedRoute } from "@angular/router";
import { Subscription } from 'rxjs';
import moment from "moment";

import * as languageLibrary from 'src/app/services/language'
import { MessageService } from "src/app/services/message.service";
import { MasterService } from "src/app/services/master.service";
import { StoreService } from "src/app/services/store.service";
import { SecurityService } from "src/app/services/security.service";

import { SALE_TYPES, SAVE_DEBOUNCE_MILLIS, TAX_METHODS } from "src/app/constants/sale";
import { Buyer } from "src/app/models/buyer.model";
import { Sale } from "src/app/models/sale.model";

@Component({
    selector: "sales-detail",
    templateUrl: "./sales-detail.component.html",
    styleUrls: ["./sales-detail.component.scss"],
    standalone: false
})

export class SalesDetailComponent implements OnInit, OnChanges, OnDestroy {

    protected language: any = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    protected words = languageLibrary.language

    protected salePrice: number = 0

    protected loading: boolean = false

    protected saving: boolean = false

    // Indicates the save button was pressed to save the detail manually
    private manual: boolean = false

    protected buyer: Buyer | null = null

    protected saleTypes: Record<number, string> = SALE_TYPES

    //  An associative array of sale tax methods with the id as the key
    protected taxMethods: Object = TAX_METHODS

    /** Default collection method as read from the lot settings. */
    protected defaultCollectionMethod: string | null = null

    protected saleId: number = 0

    protected lotId: number = 0

    /** The sale data for the current sale. */
    protected sale: Sale | null = null

    /** The sale presents for the current sale lot. */
    protected salePresets: Object | null = null

    // The collection setup for the lot associated with this sale.
    private setup: Object | null = null

    protected enableBHPH: boolean = false

    protected isQuote: boolean = true

    protected buyerId = 0

    protected buyerType: string | null = null

    // A debounce timer for search events. Used to wait for 
    // the user to stop typing before we fetch.
    private watcher: ReturnType<typeof setTimeout> | null = null

    /** Listen for component events and make decisions. */
    private componentWatcher: Subscription | null = null

    protected form: FormGroup = this.fb.group({
        saleDate: new FormControl<string>(moment().format('yyyy-MM-DD'), []),
        expires: new FormControl<string>(moment().format('yyyy-MM-DD'), []),
        type: new FormControl<number>(0, []),
        taxMethod: new FormControl<string | null>(null, []),
        locked: new FormControl<boolean>(false, []), // Unlocked
    })

    protected products: boolean = false;

    protected newSaleDownPayments: boolean = false;

    /** 
    * The last down or deferred payment date or the 
    * sale date if no down or deferred payments. Used 
    * by terms to compute first payment date. 
    */
    public soldOrLastDeferDate = null
    private lastDeferredPayDate = null

    private subscriptions: Subscription[] = []

    // Indicates whether or not the current sale is locked (no changes allowed)
    get locked(): boolean { return this.form.get('locked').value }
    get editable() { return this.locked }

    // Indicates whether or not the current sale is currently a quote
    get quote(): boolean { return this.isQuote }

    // Returns a router path based on whether this is a quote or sale
    get path(): string { return this.isQuote ? 'quotes' : 'sales' }

    get termable(): boolean { return ((this.saleType) && (['Outside', 'BHPH'].includes(this.saleTypes[this.saleType]))) }
    get outside(): boolean { return ((this.saleType) && (this.saleTypes[this.saleType] === 'Outside')) }
    get bhph(): boolean { return ((this.saleType) && (this.saleTypes[this.saleType] === 'BHPH')) }
    get sold(): string { return moment(this.form.get('saleDate').value).format('yyyy-MM-DD') }
    get wholesale(): boolean { return ((this.saleType) && (this.saleTypes[this.saleType] === 'Wholesale')) }
    get saleType() { return this.form.get('type').value }
    get saleTypeKeys() { return Object.keys(this.saleTypes) }
    get taxMethodKeys() { return Object.keys(this.taxMethods) }

    /**
    * Helper method to consistently get the list of form fees as an Array.
    */
    get taxDeferred() {
        return (this.form.get('taxMethod').value === 'DEFERRED');
    }

    constructor(private route: ActivatedRoute, private ms: MessageService, private router: Router, private master: MasterService, private store: StoreService, public security: SecurityService, private fb: FormBuilder, private location: Location) { }

    ngOnInit() {
        this.saleId = 0
        const id = parseInt(this.route.snapshot.params['id'])
        if (id && !isNaN(id) && (id > 0)) {
            this.saleId = id
        }

        const path = this.route.snapshot.url[0].path
        const pattern = new RegExp('quote', 'i')
        this.isQuote = (path && pattern.test(path))

        const suggestedSaleType = parseInt(this.route.snapshot.queryParams['type'])
        if (suggestedSaleType && !isNaN(suggestedSaleType) && (suggestedSaleType > 0)) {
            this.form.get('type').setValue(suggestedSaleType)
            this.ms.channelComponents$.emit({ message: 'saleTypeInit', section: 'contract', value: suggestedSaleType })
        }

        this.lotId = parseInt(localStorage.getItem('lot') || this.store.lotSelected)

        this.populate()

        this.componentWatcher = this.ms.channelComponents$.subscribe(async (event) => {
            if (!event || !event.message) {
                return
            }

            switch (event.message) {
                case 'lotChanged':
                    this.lotId = event.value

                    if (this.lotId == event.value) {
                        return
                    }

                    this.lotId = event.value
                    this.setup = null
                    this.salePresets = null

                    this.readCollectionSetup()
                    this.readSalesSetup()
                    break
                case 'salePriceChanged':
                    this.salePrice = event.value.baseSalePrice
                    break
                case 'buyerAdded':
                case 'buyerChanged':
                    if (this.saleId <= 0) {
                        await this.save()
                    }

                    const id: number = parseInt(event.value.id)
                    if (!id || isNaN(id) || (id <= 0)) {
                        this.buyerId = 0
                        return
                    }

                    if (this.buyerId == id) {
                        return
                    }

                    this.buyerId = id

                    break
                case 'buyerDiscarded':
                    this.buyerId = 0
                    break
                case 'buyerInit':
                case 'vehicleInit':
                case 'vehicleAdded':
                case 'vehicleChanged':
                case 'tradeAdded':
                case 'tradeChanged':
                    if (this.saleId <= 0) {
                        await this.save()
                    }

                    break
                case 'soldOrLastDeferDate':
                    this.soldOrLastDeferDate = event.value
                    break
                case 'downsChanged':
                    this.lastDeferredPayDate = event.value.last || null
                    this.determineTermComputeDate()
                    break
            }
        })
    }

    ngOnChanges(changes: SimpleChanges): void { }

    ngOnDestroy() {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
        if (this.componentWatcher) {
            this.componentWatcher.unsubscribe()
        }
        if (this.watcher) {
            clearTimeout(this.watcher)
        }
    }

    private populate = async () => {
        await this.readCollectionSetup()
        await this.readSalesSetup()

        if (this.saleId <= 0) {
            this.determineTermComputeDate()
            return
        }

        await this.read()
        this.determineTermComputeDate()
    }

    private readSalesSetup = async () => {
        if (!this.lotId && (this.lotId <= 0)) {
            return
        }

        await this.master.getAsync(`lots/${this.lotId}/salesSetup`).then(async (res) => {
            this.salePresets = res.data.salesSetup
            if (!this.salePresets) {
                return
            }

            this.ms.channelComponents$.emit({ message: 'setupRead', section: 'detail', value: this.salePresets })

            if (this.saleId > 0) {
                return
            }

            let suggestedSaleType = parseInt(this.route.snapshot.queryParams['type'])
            if (!suggestedSaleType || isNaN(suggestedSaleType) || (suggestedSaleType <= 0)) {
                suggestedSaleType = 0
            }

            let defaultSaleType = this.salePresets ? parseInt(this.salePresets['defaultSaleTypeId'] || 0) : 0
            if (!defaultSaleType || isNaN(defaultSaleType) || (defaultSaleType <= 0)) {
                defaultSaleType = 0
            }

            this.form.get('type').setValue(suggestedSaleType || defaultSaleType)
            this.form.get('taxMethod').setValue(this.salePresets['defaultBHPHTaxMethod'], { emitEvent: false })

            if (this.outside) {
                this.form.get('taxMethod').setValue('PAID')
            }
        })
    }

    /**
    *
    */
    private readCollectionSetup = async () => {
        if (this.setup) {
            return
        }

        if (!this.lotId || (this.lotId <= 0)) {
            return
        }

        await this.master.getAsync(`lots/${this.lotId}/collectionSetup`).then(async (res) => {
            this.setup = res.data.collectionSetup

            if (!this.setup || !this.setup['defaultCollectionMethod']) {
                return
            }

            if (this.outside) {
                this.defaultCollectionMethod = 'True Daily Earnings'
                return
            }

            this.defaultCollectionMethod = this.setup['defaultCollectionMethod']
        })
    }

    public read = async () => {
        if (!this.saleId || (this.saleId <= 0)) {
            return
        }

        await this.master.getAsync(`sales/${this.saleId}`).then(res => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.sale = res.data.sale
            if (!this.sale) {
                return
            }

            this.form.setValue({
                saleDate: moment(this.sale.saleDate).format('YYYY-MM-DD'),
                type: this.sale.saleFinancingTypeId,
                taxMethod: this.sale.taxMethod,
                expires: this.sale.expirationDate,
                locked: ((this.sale.locked) || this.security.notPermitted('sales', 'sale detail', 'update'))
            })

            this.salePrice = this.sale.salePrice
            if (this.locked) {
                this.form.disable({ emitEvent: false })
            }

            this.isQuote = (this.sale.saleStageId == 1) // 1 = Quote
            this.lotId = this.sale.lotId

            this.ms.channelComponents$.emit({ message: 'saleRead', section: 'sale', value: {baseSalePrice: this.sale.salePrice, totalSalePrice: this.sale.totalSalePrice, driveOutPrice: this.sale.driveOutPrice, sold: this.sold}})
            this.listen()
        })
    }

    /**
    * Listen for changes to sale date and type after the initial sale load.
    */
    private listen = () => {
        this.subscriptions.push(
            this.form.get('saleDate').valueChanges.subscribe(newValue => {
                const parsed = new Date(newValue)
                if (!parsed || (parsed.toString().toLowerCase() == 'invalid date')) {
                    return
                }

                this.save()
                this.determineTermComputeDate()
                this.ms.channelComponents$.emit({ message: 'soldChanged', section: 'contract', value: this.sold })
            })
        )

        this.subscriptions.push(
            this.form.get('expires').valueChanges.subscribe((newValue: string) => {
                if (this.watcher) {
                    clearTimeout(this.watcher)
                    this.watcher = null
                }

                if (!newValue || !moment(newValue).isValid()) {
                    return
                }

                this.watcher = setTimeout(() => {
                    const expires = moment(this.form.get('expires').value)
                    if (expires.isBefore(moment(this.sold))) {
                        this.ms.sendMessage("alert", { type: "danger", text: 'Expiration date cannot be before sale date.' })
                        return
                    }

                    this.save()
                }, SAVE_DEBOUNCE_MILLIS)
            })
        )

        this.subscriptions.push(
            this.form.get('type').valueChanges.subscribe(async (newValue: number) => {
                if (this.outside) {
                    this.form.get('taxMethod').setValue('PAID', { emitEvent: false })
                    this.form.get('taxMethod').markAsDirty()
                }

                this.save()
            })
        )

        this.subscriptions.push(
            this.form.get('taxMethod').valueChanges.subscribe((method: string) => {
                this.save()
            })
        )
    }

    /**
    * When the sale date or a down/deferred payment 
    * changes lets compute the last deferred 
    * payment date so the terms can set the first 
    * payment date.
    */
    private determineTermComputeDate = (): void => {
        if (this.lastDeferredPayDate) {
            this.soldOrLastDeferDate = moment(this.lastDeferredPayDate).format('yyyy-MM-DD')
            this.ms.channelComponents$.emit({ message: 'soldOrLastDeferDate', section: 'contract', value: this.soldOrLastDeferDate })
            return
        }

        this.soldOrLastDeferDate = moment(this.sold).format('yyyy-MM-DD')
        this.ms.channelComponents$.emit({ message: 'soldOrLastDeferDate', section: 'contract', value: this.soldOrLastDeferDate })
    }

    /**
     * 
     * @param e 
     */
    protected lockUnlock = (e: Event) => {
        e.preventDefault()
        e.stopPropagation()

        if (this.security.notPermitted('sales', 'sale detail', 'update')) {
            return
        }

        const willBeLocked = !this.form.get('locked').value
        this.form.get('locked').setValue(willBeLocked)

        this.form.enable({ emitEvent: false })
        if (willBeLocked) {
            this.form.disable({ emitEvent: false })
        }

        this.save(e)
    }

    // todo: listen nav messages
    protected listenerNav = (e) => {
        switch (e.message) {
            case 'changeLot':
                break;
            case 'changeLanguage':
                this.language = e.value
                break;
        }
    }

    /**
     * Send a post request to the api to create a new sale or 
     * a put request to update an existing sale
     * @param {Event} [e = null] An optional event associated with the save (from a button press or other user action)
     */
    public save = async (e: Event = null) => {
        if (this.saving) {
            return
        }

        const body = {
            lotId: this.lotId,
            saleDate: this.sold,
            locked: this.form.get('locked').value,
            saleFinancingTypeId: parseInt(this.form.get('type').value),
            taxMethod: this.form.get('taxMethod').value,
            saleStageId: this.isQuote ? 1 : 2, // 1 = Quote, 2 = Sale
            expirationDate: this.form.get('expires').value
        }

        this.saving = true
        if (e) {
            this.manual = true
        }

        if (this.saleId <= 0) {
            await this.add(body)
            return
        }

        await this.modify(body)
    }

    public add = async (meta) => {
        if (this.saleId > 0) {
            this.modify(meta)
            return
        }

        return await this.master.post(`sales`, { sale: meta }, (res) => {
            this.saving = false
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.saleId = res && res.data && res.data.id ? parseInt(res.data.id) : 0
            if (!this.saleId || (this.saleId <= 0)) {
                return
            }

            if (this.manual) { // manually saved? let them know it worked
                this.ms.sendMessage("alert", { type: "success", text: this.words[this.language]['success'] })
                this.manual = false
            }

            this.ms.channelComponents$.emit({ message: 'saleCreated', section: 'detail', value: this.saleId })
            this.location.replaceState(`/${this.path}/${this.saleId}`);
            this.read()
        });
    }

    public modify = async (meta) => {
        if (this.saleId <= 0) {
            return await this.add(meta)
        }

        return await this.master.put(`sales/${this.saleId}`, meta, (res) => {
            this.saving = false
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            if (this.form.get('taxMethod').dirty) {
                this.ms.channelComponents$.emit({ message: 'taxMethodChanged', section: 'terms', value: meta.taxMethod })
            }

            if (this.form.get('type').dirty) {
                this.ms.channelComponents$.emit({ message: 'saleTypeChanged', section: 'terms', value: meta.saleFinancingTypeId })
            }

            if (res && res.data) {
                this.form.get('locked').setValue(res.data.locked)

                this.form.enable({ emitEvent: false })
                if (this.locked) {
                    this.form.disable({ emitEvent: false })
                }
            }

            this.form.markAsPristine()
        });
    }

    public cancel = () => {
        if (this.saleId > 0) {
            return
        }

        this.router.navigate([`/${this.path}`])
    }

    public discard = () => {
        if (this.saleId <= 0) {
            this.router.navigate([`/${this.path}`])
            return
        }

        this.master.discard(`sales/${this.saleId}`, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.router.navigate([`/${this.path}`])
        })
    }

    /**
     * Promote a quote to a sale.
     */
    public promote = () => {
        if (this.saleId <= 0) {
            return
        }

        if (!this.isQuote) {
            return
        }

        this.master.patch(`quotes/${this.saleId}/promote`, {}, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.isQuote = false
            if (this.sale) {
                this.sale.saleStageId = 2 // 2 = Sale
            }
            
            this.router.navigate([`/sales/${this.saleId}`])
        })
    }

    public title = () => {
        if (this.saleId <= 0) {
            return `${this.words[this.language]['add']} ${this.isQuote ? this.words[this.language]['Quote'] : this.words[this.language]['Sale']}`
        }

        return `${this.isQuote ? this.words[this.language]['Quote'] : this.words[this.language]['Sale']} ${this.words[this.language]['detail']}`
    }

}
